import { useMemo } from 'react';
import { A2_DISEASE_NAME_OPTIONS } from '@/constants/consentForm';
import { useConsentMasterData } from '@/hooks/main/useConsentMasterData';

type MasterItem = { id: number; name: string };

const toIdOptions = (items?: MasterItem[]) =>
  (items ?? []).map(i => ({ label: i.name, value: i.id }));

const toNameOptions = (items?: MasterItem[]) =>
  (items ?? []).map(i => ({ label: i.name, value: i.name }));

const toStringIdOptions = (items?: MasterItem[]) =>
  (items ?? []).map(i => ({ label: i.name, value: String(i.id) }));

export const useConsentMasterOptions = () => {
  const { data: masterData } = useConsentMasterData();

  const incidentDateTypeOptions = useMemo(
    () => toIdOptions(masterData?.incident_date_types),
    [masterData?.incident_date_types]
  );
  const incidentTypeOptions = useMemo(
    () => toIdOptions(masterData?.incident_types),
    [masterData?.incident_types]
  );
  const walkingConditionOptions = useMemo(
    () => toIdOptions(masterData?.walking_conditions),
    [masterData?.walking_conditions]
  );
  const hotTreatmentTypeOptions = useMemo(
    () => toIdOptions(masterData?.hot_treatment_types),
    [masterData?.hot_treatment_types]
  );
  const diseaseNameOptions = useMemo(
    () => toStringIdOptions(masterData?.diseases),
    [masterData?.diseases]
  );
  const diseaseNameById = useMemo(() => {
    const fromMaster = new Map((masterData?.diseases ?? []).map(i => [String(i.id), i.name]));
    if (fromMaster.size > 0) return fromMaster;
    return new Map(A2_DISEASE_NAME_OPTIONS.map(i => [i.value, i.label]));
  }, [masterData?.diseases]);
  const treatmentContentOptions = useMemo(
    () => toNameOptions(masterData?.treatment_contents),
    [masterData?.treatment_contents]
  );
  const symptomMuscleOptions = useMemo(
    () => toStringIdOptions(masterData?.symptom_muscles),
    [masterData?.symptom_muscles]
  );
  const symptomJointOptions = useMemo(
    () => toStringIdOptions(masterData?.symptom_joints),
    [masterData?.symptom_joints]
  );
  const massagePartOptions = useMemo(
    () => toStringIdOptions(masterData?.massages),
    [masterData?.massages]
  );
  const orthomorphiaPartOptions = useMemo(
    () => toStringIdOptions(masterData?.orthomorphias),
    [masterData?.orthomorphias]
  );
  const walkingConditionReasonTypeOptions = useMemo(
    () =>
      (masterData?.walking_condition_reason_types ?? []).map(i => ({
        label: i.name,
        // Backward compat: 'その他' uses name as value; others use stringified id (matches A2 legacy storage).
        value: i.name === 'その他' ? i.name : String(i.id),
      })),
    [masterData?.walking_condition_reason_types]
  );

  return {
    incidentDateTypeOptions,
    incidentTypeOptions,
    walkingConditionOptions,
    hotTreatmentTypeOptions,
    diseaseNameOptions,
    diseaseNameById,
    treatmentContentOptions,
    symptomMuscleOptions,
    symptomJointOptions,
    massagePartOptions,
    orthomorphiaPartOptions,
    walkingConditionReasonTypeOptions,
  };
};
